import {ChangeDetectorRef, Component, Inject, Input, PLATFORM_ID,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {isPlatformBrowser, NgTemplateOutlet} from '@angular/common';
import {DcpComponentClassifications} from '@dcp/configurator';
import {Category, FeatureValue, Product, TranslatePipe} from '@spartacus/core';
import {CategoryService} from '@app/services/category.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {Meta} from '@angular/platform-browser';
import {MediaComponent} from '@spartacus/storefront';

export const META_TAG_KEYWORDS = 'keywords';

interface ModalConfig {
  isActive: boolean;
}

@Component({
  selector: 'dcp-classifications-template',
  templateUrl: './classifications-template.component.html',
  styleUrls: ['./classifications-template.component.scss'],
  imports: [NgTemplateOutlet, MediaComponent, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ClassificationsTemplateComponent {
  @Input() parentHeroComponent!: boolean;
  @Input() parentTechnicalInformationComponent!: boolean;
  sapCertified!: FeatureValue;
  sapCertifications!: FeatureValue[];
  solutionTypes!: string[];
  worksWith!: string[];
  worksWithModalConfig: ModalConfig = {isActive: false};
  solutionTypesModalConfig: ModalConfig = {isActive: false};
  partnerCertificationModalConfig: ModalConfig = {isActive: false};
  partnerCertificationTechnicalInfoConfig: ModalConfig = {isActive: false};
  private innerProduct!: Product;
  private innerProductClassifications: DcpComponentClassifications[] = [];

  currentFilteredCategories: Category[] = [];

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    public categoryService: CategoryService,
    private metaService: Meta,
    private productUtilitiesService: DcpProductUtilitiesService,
    private cdr: ChangeDetectorRef,
  ) {}

  get product(): Product {
    return this.innerProduct;
  }

  @Input() set product(product: Product) {
    this.innerProduct = product;
    this.prepareFeaturesAndAddTags();
    this.prepareFilteredCategories();
    this.cdr.detectChanges();
  }

  get productClassifications(): DcpComponentClassifications[] {
    return this.innerProductClassifications;
  }

  @Input() set productClassifications(productClassifications: DcpComponentClassifications[]) {
    this.innerProductClassifications = productClassifications;
    this.prepareFeaturesAndAddTags();
  }

  get hasGeneralTechnicalInformationSection(): boolean {
    return this.hasSapCertified || this.hasSolutionTypes || this.hasWorksWith || this.hasCategories;
  }

  get hasClassification(): boolean {
    return this.hasSolutionTypes || this.hasWorksWith || this.hasSapCertifications;
  }

  get hasSapCertifications(): boolean {
    return !!this.sapCertifications?.length;
  }

  get hasSapCertified(): boolean {
    return !!this.sapCertified?.value;
  }

  get hasSolutionTypes(): boolean {
    return !!this.solutionTypes?.length;
  }

  get hasWorksWith(): boolean {
    return !!this.worksWith?.length;
  }

  get hasCategories(): boolean {
    return !!this.product?.supcategories?.length;
  }

  prepareFilteredCategories(): void {
    const filteredCategories: Category[] = [];
    this.product?.supcategories?.forEach((category) => {
      if (this.categoryService.isDisplayed(category)) {
        const supcategories: Category[] = [];
        category.supcategories?.forEach((supcategory) => {
          if (this.categoryService.isDisplayed(supcategory)) {
            supcategories.push(supcategory);
          }
        });
        filteredCategories.push({
          ...category,
          supcategories,
        });
      }
    });
    this.currentFilteredCategories = filteredCategories;
  }

  prepareCertificationName(): string {
    return this.productClassifications[0]?.attributes?.name;
  }

  prepareSolutionTypesName(multiValue: boolean): string {
    return multiValue
      ? this.productClassifications[1]?.attributes?.multipleValueName
      : this.productClassifications[1]?.attributes?.name;
  }

  prepareWorksWithName(multiValue: boolean): string {
    return multiValue
      ? this.productClassifications[2]?.attributes?.multipleValueName
      : this.productClassifications[2]?.attributes?.name;
  }

  prepareFeatures() {
    this.sapCertifications = [];
    this.solutionTypes = [];
    this.worksWith = [];
    if (!this.product.classifications) {
      return;
    }
    const features = this.product.classifications[0].features;
    if (!features) {
      return;
    }
    const classifications = this.productClassifications.map((pc) =>
      features.find((f) => f.code?.includes(pc.classification)),
    );

    this.sapCertifications = classifications[0]?.featureValues ?? [];
    this.sapCertified = this.sapCertifications[0];
    this.solutionTypes =
      classifications[1]?.featureValues?.map((v) => v.value).filter((v): v is string => v != null) ?? [];
    this.worksWith = classifications[2]?.featureValues?.map((v) => v.value).filter((v): v is string => v != null) ?? [];
  }

  addTags() {
    const metaKeywords = this.productUtilitiesService.getMetaKeywords(this.innerProduct);
    if (metaKeywords) {
      this.metaService.addTag({name: META_TAG_KEYWORDS, content: metaKeywords});
    }
  }

  prepareFeaturesAndAddTags() {
    if (this.product && this.productClassifications) {
      this.prepareFeatures();
      this.addTags();
    }
  }

  displayCategory(event: Event, categoryId: string) {
    if (isPlatformBrowser(this.platformId)) {
      event.preventDefault();
      this.categoryService.displayProductsOfCategory(categoryId);
    }
  }

  displayCategoryByKeyboard(event: KeyboardEvent, categoryId: string) {
    if (isPlatformBrowser(this.platformId) && (event.key === 'Enter' || event.key === 'NumpadEnter')) {
      this.displayCategory(event, categoryId);
    }
  }

  closeModal(config: ModalConfig): void {
    if (isPlatformBrowser(this.platformId)) {
      config.isActive = false;
    }
  }

  openModal(config: ModalConfig): void {
    if (isPlatformBrowser(this.platformId)) {
      config.isActive = true;
    }
  }

  centerModal(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.dispatchEvent(new Event('resize'));
    }
  }
}
